// app/frontend/components/EventForm/utils/timezoneHelpers.ts
import timezoneMapping from '../../../entrypoints/utilities/timezones.json';
import type { RailsTimezoneData } from '../types';

interface TimezoneOption {
  value: string;
  label: string;
}

/**
 * Convert IANA timezone ID to Rails timezone name using the mapping JSON
 * @param ianaTimezone - IANA timezone ID (e.g., "America/Denver")
 * @returns Rails timezone name (e.g., "Mountain Time (US & Canada)") or null if not found
 */
export const convertIanaToRails = (ianaTimezone: string): string | null => {
  return timezoneMapping[ianaTimezone as keyof typeof timezoneMapping] || null;
};

/**
 * Convert Rails timezone name to IANA timezone ID using the mapping JSON
 * @param railsTimezone - Rails timezone name (e.g., "Mountain Time (US & Canada)")
 * @returns IANA timezone ID (e.g., "America/Denver") or null if not found
 */
export const convertRailsToIana = (railsTimezone: string): string | null => {
  const entry = Object.entries(timezoneMapping).find(([_, railsName]) => railsName === railsTimezone);
  return entry ? entry[0] : null;
};

/**
 * Create timezone options from Rails timezone data for use in Select components
 * @param railsTimezones - Array of Rails timezone data with value/label format
 * @returns Array of timezone options (passthrough since format already matches)
 */
export const createTimezoneOptionsFromRails = (railsTimezones: RailsTimezoneData[]): TimezoneOption[] => {
  // Rails data is already in the correct format for Mantine Select
  return railsTimezones.map((timezone) => ({
    value: timezone.value, // Rails timezone name
    label: timezone.label, // Rails timezone name with offset
  }));
};

/**
 * Find Rails timezone data by name
 * @param railsTimezones - Array of Rails timezone data
 * @param timezoneName - Rails timezone name to find
 * @returns Found timezone data or null
 */
export const findRailsTimezoneByName = (
  railsTimezones: RailsTimezoneData[],
  timezoneName: string,
): RailsTimezoneData | null => {
  return railsTimezones.find((tz) => tz.value === timezoneName) || null;
};

/**
 * Validate if a Rails timezone name exists in the provided data
 * @param railsTimezones - Array of Rails timezone data
 * @param timezoneName - Rails timezone name to validate
 * @returns Boolean indicating if timezone exists
 */
export const isValidRailsTimezone = (railsTimezones: RailsTimezoneData[], timezoneName: string): boolean => {
  return railsTimezones.some((tz) => tz.value === timezoneName);
};

/**
 * Get all available IANA timezone IDs from the mapping
 * @returns Array of IANA timezone IDs
 */
export const getAvailableIanaTimezones = (): string[] => {
  return Object.keys(timezoneMapping);
};

/**
 * Get all available Rails timezone names from the mapping
 * @returns Array of Rails timezone names
 */
export const getAvailableRailsTimezones = (): string[] => {
  return Object.values(timezoneMapping);
};
